import { Event } from "../../../pojo/Event";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { EventFormOccurrenceUtil } from "../../s25-event-creation-form/occurrences/event.form.occurrence.util";
import { ObjectOccurrence, ObjectOccurrenceData, ResourceOccurrence } from "../data.ingestion.service";
import { S25Util } from "../../../util/s25-util";

/**
 * Builds an {@link ObjectOccurrenceData} snapshot from a hydrated 25Live event-form model.
 *
 * The 25saas backend already understands OBJECT_OCCURRENCE answers (for dynamic object
 * approvals and as a change-detection target on resubmit), so persisting this snapshot
 * onto the hidden SYSTEM_FIELD_OBJECT_OCCURRENCE_SNAPSHOT (-4) lets the existing workflow
 * machinery handle Connect25 + 25Live integration without backend-specific branches.
 *
 * startDate / endDate intentionally use the full reservation window (rsrvStartDt / rsrvEndDt
 * semantics including setup/pre/post/takedown) since that is what approval-state computation
 * keys off of.
 */
export function extractObjectOccurrenceSnapshot(formModel: Event.Form | null | undefined): ObjectOccurrenceData {
    if (!formModel?.occurrences?.length) {
        return { occurrences: [] };
    }

    const occurrences: ObjectOccurrence[] = formModel.occurrences.map((occ, index) => {
        const reservationStart = EventFormOccurrenceUtil.getStartDt(occ);
        const reservationEnd = EventFormOccurrenceUtil.getEndDt(occ);
        const locations = (EventFormOccurrenceUtil.getOccLocations(formModel, occ, true) || []) as S25ItemI[];
        const resources = (EventFormOccurrenceUtil.getOccResources(formModel, occ, true) || []) as S25ItemI[];

        const occurrenceUuid = String((occ as any).occUUID ?? (occ as any).uuid ?? index);

        return {
            occurrenceUuid,
            startDate: toIso(reservationStart),
            endDate: toIso(reservationEnd),
            locationIds: locations
                .map((location: S25ItemI) => numericItemId(location))
                .filter((locationId): locationId is number => locationId !== undefined),
            resources: resources
                .map((resource: S25ItemI): ResourceOccurrence | null => {
                    const resourceId = numericItemId(resource);
                    if (resourceId === undefined) {
                        return null;
                    }
                    const stockLevel = resource.stock_level;
                    const actRsrcCount = typeof stockLevel === "number" && Number.isFinite(stockLevel) ? stockLevel : 1;
                    return { resourceId, actRsrcCount };
                })
                .filter((resourceEntry): resourceEntry is ResourceOccurrence => resourceEntry !== null),
        } satisfies ObjectOccurrence;
    });

    return { occurrences };
}

function toIso(value: Date | string | undefined | null): string {
    if (!value) return "";
    const asDate = value instanceof Date ? value : S25Util.date.parse(value);
    if (!asDate) return "";
    return asDate.toISOString();
}

/** Coerce {@link S25ItemI#itemId} (string | number) to a finite numeric id when possible. */
function numericItemId(item: S25ItemI): number | undefined {
    const raw = item.itemId;
    if (typeof raw === "number" && Number.isFinite(raw)) {
        return raw;
    }
    if (typeof raw === "string" && raw.trim() !== "") {
        const parsed = parseInt(raw.trim(), 10);
        return Number.isNaN(parsed) ? undefined : parsed;
    }
    return undefined;
}
